import { Injectable } from '@angular/core';

import { ApOption, ApOptions } from '../option/option.model';

/** ApFilterService provides functionality for filtering, specifically for autocompletion */
@Injectable({
    providedIn: 'root'
})
export class ApAutocompleteFilterService {
    /**
     * Filter options on a string value
     */
    public filter(filter: string, options: ApOptions, optionKey: string | string[]): ApOptions {
        if (!filter) {
            return options;
        }

        if (!options || options.length === 0) {
            return [];
        }

        const searchForValueBound = this.searchForValue.bind(this, filter || '');

        // options:string[]
        if (this.optionsAreStrings(options)) {
            return options.filter(searchForValueBound);
        }

        // options:object[]  optionKey:string
        if (this.optionKeyIsString(optionKey)) {
            return options.filter((option) => searchForValueBound(option[optionKey as string]));
        }

        // options:object[]  optionKey:string[]
        return options.filter((option) => this.filterOptionKeyValues(option, optionKey).filter(searchForValueBound).length > 0);
    }

    /**
     * Convert option to an array of labels (strings) defined by the order of `optionKeys`
     */
    public getLabelOption(option: ApOption, options: ApOptions, optionKey: string | string[]): string[] {
        if (this.optionsAreStrings(options)) {
            return [option as string];
        }
        const keys = this.optionKeyIsString(optionKey) ? [optionKey] : (optionKey as string[]);
        return keys.map((key) => option[key as string]);
    }

    public optionsAreStrings(options: ApOptions): boolean {
        return options ? typeof options[0] === 'string' : false;
    }

    public optionKeyIsString(optionKey: string | string[]): boolean {
        return typeof optionKey === 'string';
    }

    public getLabelKey(optionKey: string | string[]): string {
        return this.optionKeyIsString(optionKey) ? (optionKey as string) : optionKey[0];
    }

    /**
     * Search a string within another string
     */
    private searchForValue(search: string, string: string): boolean {
        return string?.toLowerCase().includes(search.toLowerCase());
    }

    /**
     * Transform an `ApOption` to searchable string array (leaving out any keys not listed in `optionKey:string[]`)
     */
    private filterOptionKeyValues(option: ApOption, optionKey: string | string[]): string[] {
        return Object.entries(option)
            .filter(([key]) => optionKey.includes(key))
            .map((keyAndValue) => keyAndValue[1]);
    }
}
